import React, { useCallback, useEffect, useState } from 'react';
import { getApiUrl as apiUrl } from '../services/apiConfig';
import { RiCheckLine, RiFileCopyLine, RiLinksLine, RiMovieLine, RiShareForwardLine } from 'react-icons/ri';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../services/apiConfig';
import styles from './StoryShare.module.css';

/**
 * One-tap share for the player header: creates the public link if it does
 * not exist yet, then opens the system share sheet (or copies the link).
 */
export function StoryShareButton({ jobId, className, size = 22 }) {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const share = async (event) => {
    event.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const token = await auth.getToken();
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const url = apiUrl(`/api/story-time/jobs/${encodeURIComponent(jobId)}/share`);
      let state = await fetch(url, { headers }).then((r) => r.json()).catch(() => null);
      if (!state?.enabled) {
        const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ enabled: true }) });
        state = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(state.detail || 'Could not create the link');
      }
      if (navigator.share) {
        await navigator.share({ title: 'Listen to my Story Time series', url: state.url }).catch(() => {});
      } else {
        await navigator.clipboard.writeText(state.url);
        setDone('Copied');
        setTimeout(() => setDone(''), 1800);
      }
    } catch (e) {
      setDone('Failed');
      setTimeout(() => setDone(''), 1800);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className={className} onClick={share} disabled={busy} title={done || 'Share this series'} aria-label="Share this series">
      {done === 'Copied' ? <RiCheckLine size={size} /> : <RiShareForwardLine size={size} />}
    </button>
  );
}

// Owner's control for a Story Time creation: off by default; on makes a public link; off again kills that link for good.
export default function StoryShare({ jobId }) {
  const auth = useAuth();
  const [state, setState] = useState(null);      // { enabled, url, plays, trailerUrl }
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const call = useCallback(async (method, body) => {
    const token = await auth.getToken();
    const res = await fetch(getApiUrl(`/api/story-time/jobs/${encodeURIComponent(jobId)}/share`), {
      method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Something went wrong. Try again.');
    return data;
  }, [auth, jobId]);

  useEffect(() => { let alive = true; call('GET').then((d) => alive && setState(d)).catch(() => alive && setState(null)); return () => { alive = false; }; }, [call]);

  const toggle = async (enabled) => {
    setBusy(true); setError('');
    try { setState(await call('POST', { enabled })); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(state.url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { setError('Copy failed. Select the link and copy it by hand.'); }
  };
  const nativeShare = () => navigator.share({ title: 'Listen to my Story Time series', url: state.url }).catch(() => {});

  if (!state) return null;   // not finished yet, or the check failed: show nothing rather than a broken control
  return (
    <div className={styles.share}>
      {!state.enabled ? (
        <>
          <p className={styles.note}>Only you can hear this. Share it with a link: anyone can play episode 1, and the rest with a free account.</p>
          <button type="button" className={styles.button} onClick={() => toggle(true)} disabled={busy}><RiLinksLine aria-hidden /> {busy ? 'Creating link…' : 'Create share link'}</button>
        </>
      ) : (
        <>
          <div className={styles.row}>
            <input className={styles.url} readOnly value={state.url} onFocus={(e) => e.target.select()} aria-label="Share link" />
            <button type="button" className={styles.button} onClick={copy}>{copied ? <RiCheckLine aria-hidden /> : <RiFileCopyLine aria-hidden />} {copied ? 'Copied' : 'Copy'}</button>
            {typeof navigator !== 'undefined' && navigator.share && <button type="button" className={styles.button} onClick={nativeShare}><RiShareForwardLine aria-hidden /> Share</button>}
          </div>
          {state.trailerUrl && (  /* the season trailer (founder, 26 Sep 2026): a 30 s vertical video to post on Instagram with this link */
            <div className={styles.row}>
              <a className={styles.button} href={state.trailerUrl} download><RiMovieLine aria-hidden /> Video for Instagram</a>
              <span className={styles.note}>A 30-second trailer. Post it and put this link in your caption or bio.</span>
            </div>
          )}
          <p className={styles.note}>
            Opened {state.plays} {state.plays === 1 ? 'time' : 'times'}.{' '}
            <button type="button" className={styles.link} onClick={() => toggle(false)} disabled={busy}>Stop sharing</button> (this link stops working for everyone).
          </p>
        </>
      )}
      {error && <p className={styles.error} role="alert">{error}</p>}
    </div>
  );
}
